import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, BookOpen, ChevronDown, Loader2, Search, Lightbulb,
  Sigma, ListChecks, AlertTriangle, Zap, RefreshCw, Target, GraduationCap,
  Calculator, FunctionSquare, BarChart3, Brain, Puzzle, ChevronRight,
} from "lucide-react";

// Per-module identity: icon + colour tokens (keeps the list vivid, not flat).
const MODULE_STYLE = {
  "Arithmetic": { icon: Calculator, grad: "from-indigo-500 to-blue-600", tint: "bg-indigo-50 text-indigo-700", ring: "hover:border-indigo-400", dot: "text-indigo-600" },
  "Advanced Mathematics": { icon: FunctionSquare, grad: "from-violet-500 to-purple-600", tint: "bg-violet-50 text-violet-700", ring: "hover:border-violet-400", dot: "text-violet-600" },
  "Data Interpretation & Statistics": { icon: BarChart3, grad: "from-sky-500 to-cyan-600", tint: "bg-sky-50 text-sky-700", ring: "hover:border-sky-400", dot: "text-sky-600" },
  "Logical Reasoning": { icon: Brain, grad: "from-emerald-500 to-teal-600", tint: "bg-emerald-50 text-emerald-700", ring: "hover:border-emerald-400", dot: "text-emerald-600" },
  "Analytical & Mental Ability": { icon: Puzzle, grad: "from-amber-500 to-orange-600", tint: "bg-amber-50 text-amber-700", ring: "hover:border-amber-400", dot: "text-amber-600" },
  "Reading Comprehension & Verbal Ability": { icon: BookOpen, grad: "from-rose-500 to-pink-600", tint: "bg-rose-50 text-rose-700", ring: "hover:border-rose-400", dot: "text-rose-600" },
};
const moduleStyle = (m) => MODULE_STYLE[m] || { icon: BookOpen, grad: "from-slate-500 to-slate-600", tint: "bg-slate-100 text-slate-700", ring: "hover:border-slate-400", dot: "text-slate-600" };

const token = () => localStorage.getItem("token");

// ── Sub-section: renders the full CLARIAS concept-note structure ────────────
const SubSection = ({ sub, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasEx = sub.worked_examples?.length > 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`concept-sub-${sub.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-indigo-50/40 transition-colors"
        data-testid={`concept-sub-toggle-${sub.id}`}
      >
        <span className="flex-shrink-0 rounded-md bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">{sub.id}</span>
        <span className="flex-1 text-sm font-semibold text-slate-800">{sub.title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-3">
          {sub.in_simple_words && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1 mb-1"><Lightbulb className="w-3.5 h-3.5" /> In Simple Words</p>
              <p className="text-sm text-slate-700 leading-relaxed">{sub.in_simple_words}</p>
            </div>
          )}
          {sub.images?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`concept-images-${sub.id}`}>
              {sub.images.map((im) => (
                <a key={im.id} href={`${API_BASE}/csat-concept-notes/image/${im.id}`} target="_blank" rel="noopener noreferrer"
                   className="block rounded-lg border border-slate-200 bg-slate-50 p-2 hover:border-indigo-400 transition-colors">
                  <img src={`${API_BASE}/csat-concept-notes/image/${im.id}`} alt="figure"
                       className="w-full h-auto rounded-md" loading="lazy" data-testid={`concept-img-${im.id}`} />
                </a>
              ))}
            </div>
          )}
          {sub.definition && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Definition</p>
              <p className="text-sm text-slate-700 leading-relaxed">{sub.definition}</p>
            </div>
          )}
          {sub.core_theory?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-600 flex items-center gap-1 mb-1.5"><Sigma className="w-3.5 h-3.5" /> Core Theory &amp; Formulas</p>
              <ul className="space-y-1.5">
                {sub.core_theory.map((c, i) => (
                  <li key={i} className="text-sm text-slate-700 leading-relaxed rounded-md bg-indigo-50/60 border border-indigo-100 px-3 py-1.5 font-mono">{c}</li>
                ))}
              </ul>
            </div>
          )}
          {hasEx && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-600 flex items-center gap-1 mb-1.5"><ListChecks className="w-3.5 h-3.5" /> Worked Examples</p>
              <div className="space-y-2.5">
                {sub.worked_examples.map((ex, i) => (
                  <div key={i} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid={`concept-example-${sub.id}-${i}`}>
                    <p className="text-sm font-semibold text-slate-800 mb-1.5"><span className="text-emerald-700">Q{i + 1}.</span> {ex.question}</p>
                    {ex.solution?.length > 0 && (
                      <div className="space-y-0.5 mb-1.5 pl-2 border-l-2 border-emerald-300">
                        {ex.solution.map((s, j) => (
                          <p key={j} className="text-sm text-slate-600 leading-relaxed font-mono">{s}</p>
                        ))}
                      </div>
                    )}
                    {ex.answer && <p className="text-sm font-bold text-emerald-800">✓ Answer: {ex.answer}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {sub.common_traps?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide text-rose-600 flex items-center gap-1 mb-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Common Traps</p>
              <ul className="space-y-1 list-disc pl-5">
                {sub.common_traps.map((c, i) => <li key={i} className="text-sm text-slate-700 leading-relaxed">{c}</li>)}
              </ul>
            </div>
          )}
          {sub.shortcut?.length > 0 && (
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-violet-700 flex items-center gap-1 mb-1"><Zap className="w-3.5 h-3.5" /> Shortcut / Exam Trick</p>
              <ul className="space-y-1 list-disc pl-5">
                {sub.shortcut.map((c, i) => <li key={i} className="text-sm text-slate-700 leading-relaxed">{c}</li>)}
              </ul>
            </div>
          )}
          {sub.quick_recap?.length > 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1 mb-1"><RefreshCw className="w-3.5 h-3.5" /> Quick Recap</p>
              <ul className="space-y-1 list-disc pl-5">
                {sub.quick_recap.map((c, i) => <li key={i} className="text-sm text-slate-700 leading-relaxed">{c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Chapter viewer ──────────────────────────────────────────────────────────
const ChapterView = ({ slug, onBack, onPractice }) => {
  const [ch, setCh] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/csat-concept-notes/chapter/${slug}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setCh(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>;
  if (!ch) return null;

  let globalIdx = 0;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="concept-chapter-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="concept-chapter-back">
        <ArrowLeft className="w-4 h-4" /> All chapters
      </button>

      <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 p-5 text-white">
        <p className="text-xs text-white/80 uppercase tracking-wide">{ch.module}</p>
        <h1 className="text-2xl font-bold">{ch.chapter}</h1>
        <div className="flex items-center gap-3 mt-2 text-sm text-white/90">
          <span>{ch.sections?.length || 0} sections</span>
          <span>·</span>
          <span>{ch.sections?.reduce((a, s) => a + (s.subsections?.length || 0), 0)} topics</span>
        </div>
        <Button onClick={() => onPractice?.(ch.chapter)} className="mt-3 bg-white text-indigo-700 hover:bg-indigo-50" size="sm" data-testid="concept-practice-btn">
          <Target className="w-4 h-4 mr-1.5" /> Practice this chapter
        </Button>
      </div>

      {ch.sections?.map((sec) => (
        <div key={sec.id} className="space-y-2.5">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-600 text-xs font-bold text-white">{sec.id}</span>
            {sec.title}
          </h2>
          {sec.subsections?.map((sub) => {
            const isFirst = globalIdx === 0;
            globalIdx += 1;
            return <SubSection key={sub.id} sub={sub} defaultOpen={isFirst} />;
          })}
        </div>
      ))}
    </div>
  );
};

// ── Chapter list ────────────────────────────────────────────────────────────
const CSATConceptNotes = ({ onBack, onPractice }) => {
  const [modules, setModules] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/csat-concept-notes/chapters`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setModules(data.modules || []);
        setTotal(data.total_chapters || 0);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (active) return <ChapterView slug={active} onBack={() => setActive(null)} onPractice={onPractice} />;

  const ql = q.trim().toLowerCase();
  const filtered = modules
    .map((m) => ({ ...m, chapters: m.chapters.filter((c) => !ql || c.chapter.toLowerCase().includes(ql) || m.module.toLowerCase().includes(ql)) }))
    .filter((m) => m.chapters.length > 0);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="concept-notes-list">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="concept-notes-back">
        <ArrowLeft className="w-4 h-4" /> CSAT Suite
      </button>

      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
          <BookOpen className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Learn Concepts</h1>
          <p className="text-sm text-slate-500">Notes, formulas &amp; solved examples · {total} chapters</p>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chapters…" className="pl-9" data-testid="concept-search" />
      </div>

      {loading ? (
        <div className="min-h-[30vh] flex items-center justify-center"><Loader2 className="w-7 h-7 text-indigo-600 animate-spin" /></div>
      ) : total === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600">No concept notes yet. An admin can upload chapter files from the admin panel.</p>
          </CardContent>
        </Card>
      ) : (
        filtered.map((m) => {
          const st = moduleStyle(m.module);
          const MIcon = st.icon;
          return (
          <div key={m.module} className="space-y-2.5" data-testid={`concept-module-${m.module}`}>
            <div className="flex items-center gap-2">
              <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${st.tint}`}>
                <MIcon className="w-4 h-4" />
              </span>
              <h2 className="text-sm font-bold text-slate-800">{m.module}</h2>
              <Badge variant="outline" className="text-[10px] text-slate-500">{m.chapters.length} chapters</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {m.chapters.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setActive(c.slug)}
                  className={`group flex items-center gap-3 text-left rounded-xl border border-slate-200 bg-white p-3.5 ${st.ring} hover:shadow-md transition-all`}
                  data-testid={`concept-chapter-${c.slug}`}
                >
                  <span className={`flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${st.grad} text-white shadow-sm group-hover:scale-105 transition-transform`}>
                    <MIcon className="w-5 h-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-800 group-hover:text-slate-900 truncate">{c.chapter}</span>
                    <span className="block text-xs text-slate-400 mt-0.5">{c.subsection_count} topics · {c.example_count} examples</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
                </button>
              ))}
            </div>
          </div>
          );
        })
      )}
    </div>
  );
};

export default CSATConceptNotes;
